// Quotes — list + create + edit. New quote opens a modal with the shared
// LineItemEditor. Existing quotes can be edited inline (description / line
// items / status).
import { useEffect, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { useToast, ToastBanner } from '../../components/Toast'
import { apiFetch } from '../../lib/api'
import LineItemEditor, { LineItemDraft, lineItemsTotal } from './LineItemEditor'

interface Quote {
  id: string; number: number; customerName: string; customerEmail: string
  status: string; totalCents: number; currency: string
  validUntil: string | null; notes: string | null
  lineItems: Array<LineItemDraft & { lineTotalCents?: number }>
  createdAt: string; sentAt: string | null; acceptedAt: string | null
}

const STATUS_TONE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-800',
  accepted: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-amber-100 text-amber-800',
}

export default function Quotes() {
  const { getAccessToken } = useAuth()
  const [items, setItems] = useState<Quote[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Quote | null>(null)
  const [draft, setDraft] = useState<{ customerName: string; customerEmail: string; validUntil: string; notes: string; lineItems: LineItemDraft[] }>({ customerName: '', customerEmail: '', validUntil: '', notes: '', lineItems: [] })
  const { toast, show } = useToast()

  async function load() {
    try {
      const d = await apiFetch<{ items: Quote[] }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/quotes')
      setItems(d.items); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }
  useEffect(() => { void load() /* eslint-disable-next-line */ }, [])

  function openCreate() {
    setDraft({ customerName: '', customerEmail: '', validUntil: '', notes: '', lineItems: [] })
    setCreating(true)
  }
  function openEdit(q: Quote) {
    setDraft({
      customerName: q.customerName,
      customerEmail: q.customerEmail,
      validUntil: q.validUntil ?? '',
      notes: q.notes ?? '',
      lineItems: q.lineItems.map(({ description, qty, unitPriceCents }) => ({ description, qty, unitPriceCents })),
    })
    setEditing(q)
  }

  async function save() {
    if (!draft.customerName || !draft.customerEmail) { alert('Customer name + email required'); return }
    try {
      if (editing) {
        await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/quotes/${editing.id}`, { method: 'PATCH', body: JSON.stringify({ ...draft, validUntil: draft.validUntil || null }) })
      } else {
        await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/quotes', { method: 'POST', body: JSON.stringify({ ...draft, validUntil: draft.validUntil || null }) })
      }
      setCreating(false); setEditing(null)
      await load()
      show(editing ? 'Quote updated.' : 'Quote created.')
    } catch (e) { show((e as Error).message, 'error', 5000) }
  }
  async function setStatus(id: string, status: string) {
    // Status flips in the dropdown can be confusing — adding a confirm
    // step for the irreversible-feeling transitions (rejected / expired)
    // and the "sent" stamp (which sets sentAt server-side and is the
    // visible commitment to the customer).
    const needsConfirm = status === 'sent' || status === 'rejected' || status === 'expired'
    if (needsConfirm && !confirm(`Set this quote to "${status}"? This is hard to reverse cleanly.`)) {
      await load() // revert the optimistic <select> change
      return
    }
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/quotes/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) })
      await load()
    } catch (e) { alert((e as Error).message) }
  }
  async function remove(id: string) {
    if (!confirm('Delete this quote?')) return
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/quotes/${id}`, { method: 'DELETE' })
      await load()
    } catch (e) { alert((e as Error).message) }
  }

  const isModalOpen = creating || !!editing

  return (
    <div className="p-8">
      <ToastBanner toast={toast} />
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="eyebrow">Processing</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Quotes</h1>
        </div>
        <button onClick={openCreate} className="btn-gold">+ New quote</button>
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
            <tr>
              <th className="text-left px-4 py-2 font-bold">Quote</th>
              <th className="text-left px-4 py-2 font-bold">Customer</th>
              <th className="text-left px-4 py-2 font-bold">Status</th>
              <th className="text-right px-4 py-2 font-bold">Total</th>
              <th className="text-right px-4 py-2 font-bold">Valid until</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-3 text-sm">No quotes yet.</td></tr>}
            {items.map((q) => (
              <tr key={q.id} className="hover:bg-surface transition-colors">
                <td className="px-4 py-3"><button onClick={() => openEdit(q)} className="font-mono text-xs text-ink-2 hover:text-gold-dark">Q-{q.number}</button></td>
                <td className="px-4 py-3"><div className="text-ink">{q.customerName}</div><div className="text-xs text-ink-3">{q.customerEmail}</div></td>
                <td className="px-4 py-3">
                  <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)}
                    className={'text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border-0 ' + (STATUS_TONE[q.status] ?? 'bg-gray-100 text-gray-700')}>
                    <option value="draft">draft</option><option value="sent">sent</option><option value="accepted">accepted</option><option value="rejected">rejected</option><option value="expired">expired</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-ink">${(q.totalCents / 100).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-xs text-ink-3">{q.validUntil ?? '—'}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => remove(q.id)} className="text-ink-3 hover:text-red-600">×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading uppercase font-bold text-lg text-ink mb-4">{editing ? `Edit quote ${editing.id.slice(0,8)}…` : 'New quote'}</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div><div className="eyebrow">Customer name</div><input value={draft.customerName} onChange={(e) => setDraft({ ...draft, customerName: e.target.value })} className="input" /></div>
              <div><div className="eyebrow">Customer email</div><input type="email" value={draft.customerEmail} onChange={(e) => setDraft({ ...draft, customerEmail: e.target.value })} className="input" /></div>
              <div><div className="eyebrow">Valid until</div><input type="date" value={draft.validUntil} onChange={(e) => setDraft({ ...draft, validUntil: e.target.value })} className="input" /></div>
            </div>
            <div className="mt-4"><div className="eyebrow">Line items</div>
              <LineItemEditor items={draft.lineItems} onChange={(li) => setDraft({ ...draft, lineItems: li })}
                footer={<div className="text-sm font-semibold text-ink">Total ${(lineItemsTotal(draft.lineItems) / 100).toFixed(2)}</div>} />
            </div>
            <div className="mt-3"><div className="eyebrow">Notes</div><textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="input" /></div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setCreating(false); setEditing(null) }} className="btn-outline flex-1 justify-center">Cancel</button>
              <button onClick={save} className="btn-gold flex-1 justify-center">{editing ? 'Save changes' : 'Create quote'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
